import React, { useState } from "react";
import { RiFileCopyLine } from "react-icons/ri";
import { FiMinus, FiPlus } from "react-icons/fi";
import { addAndRemoveLoadingIcon } from "../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection";
import { ItemPage } from "../../general_components/content_management/item_page/itemPage";
import { AddButton, ThreeDotsButton } from "../../general_components/content_management/reusableItems/buttons";
import { CustomAddForm } from "../../general_components/forms/CustomAddForm";
import { ConfirmationDialogue } from "../../general_components/forms/ConfirmationDialogue";
import { checkForEmptyField } from "../../general_components/forms/Form";
import { COMPANY_SECTION_UPDATE, CUSTOM_SECTION_UPDATE_FORM_GENERATOR, generateColumn } from "../../supplementary_components/itemEditMethods";
import { API_ROOT, sendRequest } from "../../supplementary_components/requests";
import { addNotification, addPopup } from "../../supplementary_components/store";
import { can } from "../../supplementary_components/userDataDelegate";
import {
    formatWeeklyGrossResetHourLabel,
    parseWeeklyGrossResetHourLabel,
    weeklyGrossResetHourDataset,
} from "./weeklyGrossResetHour";
import styles from "./companyPage.module.scss";

const TELEGRAM_BOT_ACTIONS = [
    { key: "addLoadNote", label: "Add Load Note" },
    { key: "addLoad", label: "Add Load" },
    { key: "editLoad", label: "Edit Load" },
];

const INTEGRATION_CATALOG = [
    { provider: "SPIREON", allowsMultiple: true, fields: ["username", "token", "password"] },
    { provider: "SKYBITZ", allowsMultiple: true, fields: ["username", "password"] },
    { provider: "FLEETVIEW", allowsMultiple: true, fields: ["token"] },
    { provider: "SAMSARA", allowsMultiple: false, fields: ["token"] },
    { provider: "MOTIVE", allowsMultiple: false, fields: ["token"] },
    { provider: "EFS", allowsMultiple: false, fields: ["username", "password"], hasMoneyCodeLimits: true },
    { provider: "VECTOR", allowsMultiple: false, fields: ["bearerKey", "uuid"] },
];

const ALL_INTEGRATION_FIELDS = ["username", "token", "password", "bearerKey", "uuid"];
const CREDENTIAL_FIELDS = new Set(["password", "token", "bearerKey"]);

const MONEY_CODE_PURPOSE_LIMITS = [
    { key: "Invoice", label: "Invoice Limit", defaultValue: "" },
    { key: "CashAdvance", label: "Cash Advance Limit", defaultValue: "250" },
    { key: "Fuel", label: "Fuel Limit", defaultValue: "1000" },
    { key: "Scale", label: "Scale Limit", defaultValue: "50" },
    { key: "LumperFee", label: "Lumper Fee Limit", defaultValue: "200" },
    { key: "Accessories", label: "Accessories Limit", defaultValue: "1000" },
    { key: "Parking", label: "Parking Limit", defaultValue: "1000" },
    { key: "Other", label: "Other Limit", defaultValue: "500" },
];

const FIELD_LABELS = {
    username: "Username",
    token: "Token / API Key",
    password: "Password",
    bearerKey: "Bearer Key",
    uuid: "UUID",
};

const fieldsForProvider = (provider) => {
    const entry = INTEGRATION_CATALOG.find((p) => p.provider === provider);
    return entry ? entry.fields : [];
};

const providerHasMoneyCodeLimits = (provider) => {
    const entry = INTEGRATION_CATALOG.find((p) => p.provider === provider);
    return !!entry?.hasMoneyCodeLimits;
};

const applyProviderFieldVisibility = (formTarget, currentlySelectedProvider) => {
    const availableFieldNames = fieldsForProvider(currentlySelectedProvider);
    for (const fieldName of ALL_INTEGRATION_FIELDS) {
        let inputField = formTarget.querySelector(`input[name="${fieldName}"]`);
        if (!inputField) {
            inputField = formTarget.querySelector(`select[name="${fieldName}"]`);
        }
        if (!inputField) continue;
        const show = availableFieldNames.includes(fieldName);
        inputField.disabled = !show;
        inputField.parentNode.style.display = show ? "block" : "none";
    }
    const showLimits = providerHasMoneyCodeLimits(currentlySelectedProvider)
        && !!formTarget.querySelector(`input[name="id"]`);
    for (const purpose of MONEY_CODE_PURPOSE_LIMITS) {
        const inputField = formTarget.querySelector(`input[name="mcLimit_${purpose.key}"]`);
        if (!inputField) continue;
        inputField.disabled = !showLimits;
        inputField.parentNode.style.display = showLimits ? "block" : "none";
    }
};

const limitDefaultForEdit = (editItem, purpose) => {
    const stored = editItem?.settings?.moneyCodePurposeLimits?.[purpose.key];
    if (stored === null || stored === undefined || stored === "") {
        return purpose.key === "Invoice" ? "" : (purpose.defaultValue ?? "");
    }
    return String(stored);
};

const IntegrationRecordForm = (existingIntegrations = [], editItem = null) => {
    const usedProviders = new Set(
        (Array.isArray(existingIntegrations) ? existingIntegrations : [])
            .map((item) => item?.provider)
            .filter(Boolean)
    );
    const availableProviders = editItem
        ? [editItem.provider]
        : [
              "",
              ...INTEGRATION_CATALOG
                  .filter((p) => p.allowsMultiple || !usedProviders.has(p.provider))
                  .map((p) => p.provider),
          ];
    const showLimitsOnEdit = editItem && providerHasMoneyCodeLimits(editItem.provider);

    const columns = [
        ...(editItem
            ? [
                  {
                      hiddenField: true,
                      name: "id",
                      defaultValue: () => editItem.id,
                  },
              ]
            : []),
        {
            label: "Provider",
            name: "provider",
            type: "select",
            mandatory: true,
            dataset: availableProviders,
            defaultValue: () => (editItem ? editItem.provider : ""),
            onChange: (val) => {
                let target = val.target;
                while (target.tagName !== "FORM") {
                    target = target.parentNode;
                }
                applyProviderFieldVisibility(target, val.target.value);
            },
        },
        ...ALL_INTEGRATION_FIELDS.map((fieldName) => {
            const visibleOnEdit = editItem && fieldsForProvider(editItem.provider).includes(fieldName);
            const isCredential = CREDENTIAL_FIELDS.has(fieldName);
            const publicDefault =
                editItem && !isCredential && editItem[fieldName] != null ? editItem[fieldName] : "";
            const keepHint = editItem && isCredential ? " (leave blank to keep)" : "";
            return {
                label: FIELD_LABELS[fieldName] + keepHint,
                name: fieldName,
                // Credentials required on add; optional on edit (blank = keep current).
                mandatory: editItem ? !isCredential : true,
                autoComplete: "off",
                customDisplayMode: visibleOnEdit ? "block" : "none",
                disabled: !visibleOnEdit,
                defaultValue: () => (visibleOnEdit && !isCredential ? publicDefault : ""),
            };
        }),
        ...MONEY_CODE_PURPOSE_LIMITS.map((purpose) => ({
            label: purpose.label + (purpose.key === "Invoice" ? " (blank = unlimited)" : ""),
            name: `mcLimit_${purpose.key}`,
            type: "number",
            mandatory: false,
            autoComplete: "off",
            customDisplayMode: showLimitsOnEdit ? "block" : "none",
            disabled: !showLimitsOnEdit,
            defaultValue: () => (showLimitsOnEdit ? limitDefaultForEdit(editItem, purpose) : ""),
        })),
    ];

    return { columns };
};

export class COMPANY_PAGE_OBJECT {

    generalInfo = ()=>{
        return [
            {
                label: 'Comapny Name',
                val: this.data.companyName,
                nameInAddConfForm: 'companyName',
                defaultValue:()=>{
                    return this.companyName
                }
            },
            generateColumn(this.data, false, "MC Number", "mcNumber"),
            generateColumn(this.data, false, "DOT Number", "dotNumber"),
            generateColumn(this.data, false, "Address Line 1", "addressLine1"),
            generateColumn(this.data, false, "Address Line 2", "addressLine2"),
            generateColumn(this.data, false, "City", "city"),
            generateColumn(this.data, false, "State", "state"),
            generateColumn(this.data, false, "Postal Code", "postalCode"),
            
        ]
    }
    operationalInfo = () => {
        return [
            generateColumn(this.data, true, 'Operations Manager', [['operationsManager', 'firstName'], " ", ['operationsManager', 'lastName']], {
                autoComplete:"off",
                suggestionsEnabled: true,
                dataValueRequired : true,
                
                suggestionsLink: API_ROOT+'employees/managerByName?pattern=',
                
                afterMinimumChars:1,
                displayValueGenerator: (val)=>{return `${val["firstName"]} ${val["lastName"]}`;},
            }),
            generateColumn(this.data, true, "Majority Drivers", 'mainlyOperatesWithSolo',{
                type: 'select',
                dataset: ["Solo", "Team"],
                customDisplayFormat : (val)=>{return val ? "Solo" : "Team"},
                processBeforeSending : (val)=>{
                    localStorage.setItem('mainlyOperatesWithSolo', val === "Solo");
                    return val === "Solo"},
                defaultValue : ()=>{return this.data?.mainlyOperatesWithSolo ? "Solo" : "Team"},
            }),
            generateColumn(this.data, true, "Owner/Lease Statement Period", 'ownerLeasePayoutFollowsWeeklyRateDistribution',{
                type: 'select',
                dataset: ["Weekly Rate Allocation", "Delivery Date"],
                customDisplayFormat : (val)=>{return val === true ? "Weekly Rate Allocation" : "Delivery Date"},
                processBeforeSending : (val)=>{
                    const follows = val === "Weekly Rate Allocation";
                    localStorage.setItem('ownerLeasePayoutFollowsWeeklyRateDistribution', JSON.stringify(follows));
                    try {
                        const list = JSON.parse(localStorage.getItem('accessibleCompanies') || '[]');
                        const id = localStorage.getItem('currentCompanyId');
                        const next = (Array.isArray(list) ? list : []).map((c) =>
                            String(c?.id) === String(id)
                                ? { ...c, ownerLeasePayoutFollowsWeeklyRateDistribution: follows }
                                : c
                        );
                        localStorage.setItem('accessibleCompanies', JSON.stringify(next));
                    } catch (_) {}
                    return follows;
                },
                defaultValue : ()=>{
                    return this.data?.ownerLeasePayoutFollowsWeeklyRateDistribution === true
                        ? "Weekly Rate Allocation"
                        : "Delivery Date";
                },
            }),
            generateColumn(this.data, true, "Truck Status Column", 'truckStatusColumnEnabled',{
                type: 'select',
                dataset: ["Enabled", "Disabled"],
                customDisplayFormat : (val)=>{return val === false || val === 'false' ? "Disabled" : "Enabled"},
                processBeforeSending : (val)=>{
                    const enabled = val !== "Disabled";
                    localStorage.setItem('truckStatusColumnEnabled', JSON.stringify(enabled));
                    try {
                        const list = JSON.parse(localStorage.getItem('accessibleCompanies') || '[]');
                        const id = localStorage.getItem('currentCompanyId');
                        const next = (Array.isArray(list) ? list : []).map((c) =>
                            String(c?.id) === String(id)
                                ? { ...c, truckStatusColumnEnabled: enabled }
                                : c
                        );
                        localStorage.setItem('accessibleCompanies', JSON.stringify(next));
                    } catch (_) {}
                    return enabled;
                },
                defaultValue : ()=>{
                    return this.data?.truckStatusColumnEnabled === false ? "Disabled" : "Enabled";
                },
            }),
            generateColumn(this.data, true, "Weekly Gross Reset Time", 'weeklyGrossResetHour',{
                type: 'select',
                dataset: weeklyGrossResetHourDataset(),
                customDisplayFormat: (val) => formatWeeklyGrossResetHourLabel(val),
                processBeforeSending: (val) => parseWeeklyGrossResetHourLabel(val),
                defaultValue: () => formatWeeklyGrossResetHourLabel(this.data?.weeklyGrossResetHour),
            }),
        ]
    }
    contactInfo = () => {
        return [
            generateColumn(this.data, false, "Email", "email"),
            generateColumn(this.data, true, "Dispatch Email", "dispatchEmail"),
            generateColumn(this.data, true, "Accounting Email", "accountingEmail"),
            generateColumn(this.data, false, "Phone Number", "phoneNumber"),
            generateColumn(this.data, true, "Fax", "fax"),
        ]
    }
    factoringInfo = () => {
        const usesKey = this.data?.triumphUsesSshKey === true;
        const login = this.data?.usernameForTriumphInvoicing;
        const passwordSet = !!(this.data?.passwordForTriumphInvoicing && String(this.data.passwordForTriumphInvoicing).trim());
        return [
            {
                label: "Login",
                val: login ? login : "-",
            },
            usesKey
                ? {
                    label: "Private Key",
                    val: this.data?.triumphPrivateKeySet ? "Set" : "Not set",
                }
                : {
                    label: "Password",
                    val: passwordSet ? "Set" : "Not set",
                },
        ];
    }
    editFactoring = (setDataMethod) => {
        const usesKey = this.data?.triumphUsesSshKey === true;
        // Empty inputs are written back as the string "null" by the shared form.
        // Treat that as blank so a secret left empty keeps the stored value.
        const blankToNull = (val) => {
            if (val == null || val === "" || val === "null") return null;
            return val;
        };
        const columns = () => [
            {
                notInAddForm: true,
                label: "Login",
                name: "usernameForTriumphInvoicing",
                mandatory: false,
                autoComplete: "off",
                defaultValue: () => this.data?.usernameForTriumphInvoicing || "",
                processBeforeSending: (val) => {
                    const cleaned = blankToNull(val);
                    return cleaned == null ? null : String(cleaned).trim();
                },
            },
            usesKey
                ? {
                    notInAddForm: true,
                    label: "Private Key (leave blank to keep)",
                    name: "privateKeyForTriumphInvoicing",
                    type: "textArea",
                    rowCount: 8,
                    mandatory: false,
                    autoComplete: "off",
                    defaultValue: () => "",
                    processBeforeSending: blankToNull,
                }
                : {
                    notInAddForm: true,
                    label: "Password (leave blank to keep)",
                    name: "passwordForTriumphInvoicing",
                    mandatory: false,
                    autoComplete: "off",
                    defaultValue: () => "",
                    processBeforeSending: blankToNull,
                },
        ];
        CUSTOM_SECTION_UPDATE_FORM_GENERATOR(
            "Factoring Company",
            API_ROOT + "companies/" + this.data.id + "/factoring",
            "Save Changes",
            "Factoring company was updated",
            columns,
            setDataMethod,
            false
        );
    }
    
    constructor(){
        this.getLink = API_ROOT+'companies/';
        this.columnProportions = "25rem 1fr 0.7fr";
    }
    setData(data){
        this.data = data;
        this.id = data['id'];
        this.notes = data['boardNotes'];
        // this.truckNumber = data['truckNumber'];
        this.companyName = data['companyName'];
        // this.firstDriverFN = NestedValueExtractor(data, [["driver1", "firstName"], ' ', ['driver1', 'lastName']]);
        
        this.header = {
            title : this.companyName,
            
        };
        this.infoSections = [
            {
                title: 'Company Details',
                editAccessLevel: { key: 'COMPANY_SETTINGS', level: 'MANAGE' },
                editButton : {
                    text: 'Edit',
                    action: (setDataMethod)=>{
                        COMPANY_SECTION_UPDATE(setDataMethod, this.data.id, this.generalInfo);
                        // TRAILER_SECTION_UPDATE(setDataMethod, this.data.id, this.trailerDetailsSectionColumns);
                    }
                },
                columns : this.generalInfo()
            },
            {
                title: "Operational Details",
                editAccessLevel: { key: 'COMPANY_SETTINGS', level: 'MANAGE' },
                editButton : {
                    text: 'Edit',
                    action: (setDataMethod)=>{
                        COMPANY_SECTION_UPDATE(setDataMethod, this.data.id, this.operationalInfo);
                    }
                },
                columns : this.operationalInfo()
            },
            {
                title: "Contant Info",
                editAccessLevel: { key: 'COMPANY_SETTINGS', level: 'MANAGE' },
                editButton : {
                    text: 'Edit',
                    action: (setDataMethod)=>{
                        COMPANY_SECTION_UPDATE(setDataMethod, this.data.id, this.contactInfo);
                    }
                },
                columns : this.contactInfo()
            },
            {
                title: "Factoring Company",
                editAccessLevel: { key: 'COMPANY_SETTINGS', level: 'MANAGE' },
                editButton : {
                    text: 'Edit',
                    action: (setDataMethod)=>{
                        this.editFactoring(setDataMethod);
                    }
                },
                columns : this.factoringInfo()
            },
        ]
        this.overviewSection = [
            {
                title : `Integrations`,
                content: (setDataMethod)=>{
                    return <IntegrationsManager data={this.data?.companyIntegrations} setDataMethod={setDataMethod} />
                }
            },
            {
                title: `Telegram Bot`,
                editAccessLevel: { key: 'COMPANY_SETTINGS', level: 'MANAGE' },
                editButton: {
                    text: this.data?.telegramBotApiKey ? 'Regenerate' : 'Generate',
                    action: (setDataMethod, e)=>{
                        const hadKey = !!this.data?.telegramBotApiKey;
                        addAndRemoveLoadingIcon(e.target)
                        sendRequest(
                            'POST',
                            API_ROOT + 'companies/' + this.id + '/generateTelegramBotApiKey',
                            null,
                            (updatedCompany)=>{
                                setDataMethod(prev => ({
                                    ...prev,
                                    telegramBotApiKey: updatedCompany.telegramBotApiKey
                                }))
                                addNotification({
                                    message: hadKey ? 'Telegram bot API key regenerated' : 'Telegram bot API key generated',
                                    type: 'notify',
                                    duration: '1000'
                                });
                                addAndRemoveLoadingIcon(e.target, true)
                            },
                            ()=>{
                                addNotification({message: `Something went wrong`, type: 'alert', duration: '1000'});
                                addAndRemoveLoadingIcon(e.target, true)
                            }
                        );
                    }
                },
                content: (setDataMethod)=>{
                    return (
                        <TelegramBotSettingsManager
                            companyId={this.id}
                            apiKey={this.data?.telegramBotApiKey}
                            permissions={this.data?.telegramBotPermissions}
                            setDataMethod={setDataMethod}
                        />
                    );
                }
            },

            
        ]
        this.notesSection = {

        }
    }
}
export const CompanySettingsPage = () =>{
    if (!can('COMPANY_SETTINGS', 'VIEW')) {
        return <div>You are not authorized to access this page</div>
    }
    return <ItemPage conf={{
        pageConf : new COMPANY_PAGE_OBJECT()
    }} identifier={localStorage.getItem("currentCompanyId")} />
}

const IntegrationsManager = ({ data, setDataMethod }) => {
    const normalized = !data ? [] : Array.isArray(data) ? data : [data];

    const buildIntegrationBody = (form, target) => {
        const body = {};
        const moneyCodePurposeLimits = {};
        let hasLimitFields = false;

        for (const clmn of form["columns"]) {
            if (clmn.hiddenField) continue;
            let inputField = target.querySelector(`input[name="${clmn.name}"]`);
            if (!inputField) {
                inputField = target.querySelector(`select[name="${clmn.name}"]`);
            }
            if (!inputField || inputField.disabled) continue;

            if (clmn.name.startsWith("mcLimit_")) {
                hasLimitFields = true;
                const purpose = clmn.name.slice("mcLimit_".length);
                const raw = inputField.value.trim();
                moneyCodePurposeLimits[purpose] = raw === "" ? null : Number(raw);
                continue;
            }

            // Credentials: omit blank on edit so backend keeps existing secrets.
            if (CREDENTIAL_FIELDS.has(clmn.name) && !inputField.value) {
                continue;
            }
            body[clmn.name] = inputField.value;
        }

        const idField = target.querySelector(`input[name="id"]`);
        if (idField?.value) {
            body.id = idField.value;
        }

        if (hasLimitFields && (body.provider === "EFS" || form.columns.some((c) => c.name === "provider"))) {
            const providerField = target.querySelector(`select[name="provider"]`);
            const provider = body.provider || providerField?.value;
            if (provider === "EFS") {
                body.settings = { moneyCodePurposeLimits };
            }
        }

        return body;
    };

    const showIntegrationForm = (form, buttonLabel) => {
        addPopup({
            content: CustomAddForm,
            popupData: {
                action: (event, postResultAction) => {
                    event.preventDefault();
                    const target = event.target;
                    if (checkForEmptyField(form, event)) {
                        return;
                    }
                    const body = buildIntegrationBody(form, target);
                    postResultAction(false);
                    sendRequest(
                        "POST",
                        API_ROOT + "companies/updateIntegration",
                        null,
                        (res) => {
                            setDataMethod((prev) => ({
                                ...prev,
                                companyIntegrations: res,
                            }));
                            postResultAction(true);
                        },
                        () => {
                            postResultAction(false);
                            addNotification({ message: `Something went wrong`, type: "alert" });
                        },
                        body
                    );
                },
                name: buttonLabel,
                formTitle: buttonLabel + " Integration",
                submitTitle: buttonLabel,
                method: "POST",
                columns: form["columns"],
            },
            EZCloseAllowed: true,
        });
    };

    const confirmRemoveIntegration = (item) => {
        const label = item.displayName || item.provider || "this integration";
        addPopup({
            content: ConfirmationDialogue,
            popupData: {
                confirmationDialogue: true,
                action: (postResultAction) => {
                    sendRequest(
                        "POST",
                        API_ROOT + "companies/removeIntegration?id=" + encodeURIComponent(item.id),
                        null,
                        (res) => {
                            setDataMethod((prev) => ({
                                ...prev,
                                companyIntegrations: res,
                            }));
                            postResultAction(true);
                        },
                        () => {
                            addNotification({ message: "Something went wrong", type: "alert" });
                            postResultAction(false);
                        },
                        null
                    );
                },
                cancelAction: (closePopup) => {
                    closePopup();
                },
                name: "Confirm",
                formTitle: "Remove Integration",
                submitTitle: "Remove",
                message: `Are you sure you want to remove ${label}?`,
            },
            postSuccessAction: () => {},
            EZCloseAllowed: false,
        });
    };

    return (
        <div className={styles.integrationsManager}>
            <div className={styles.integrationsGrid}>
                {normalized.map((item, index) => (
                    <div key={item.id || index} className={styles.integrationBox}>
                        <ThreeDotsButton
                            position="absolute"
                            right="0.5rem"
                            onRemove={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                confirmRemoveIntegration(item);
                            }}
                            onEdit={() => {
                                showIntegrationForm(IntegrationRecordForm(normalized, item), "Edit");
                            }}
                        />
                        <span className={styles.integrationBoxName}>
                            {item.displayName || item.provider}
                        </span>
                    </div>
                ))}
            </div>
            <AddButton
                customSize={"1.6rem"}
                action={() => {
                    showIntegrationForm(IntegrationRecordForm(normalized), "Add");
                }}
            />
        </div>
    );
};

const TelegramBotSettingsManager = ({ companyId, apiKey, permissions, setDataMethod }) => {
    const [savingKey, setSavingKey] = useState(null);
    const perms = permissions && typeof permissions === "object" ? permissions : {};

    const copyKey = () => {
        if (!apiKey) return;
        navigator.clipboard.writeText(apiKey).then(
            () => addNotification({ message: "API key copied", type: "notify", duration: "1000" }),
            () => addNotification({ message: "Failed to copy", type: "alert", duration: "1000" })
        );
    };

    const togglePermission = (actionKey) => {
        if (savingKey) return;
        const next = { ...perms, [actionKey]: !perms[actionKey] };
        // Drop explicit falses so stored shape stays sparse; backend treats missing as deny.
        TELEGRAM_BOT_ACTIONS.forEach(({ key }) => {
            if (!next[key]) delete next[key];
        });
        setSavingKey(actionKey);
        sendRequest(
            "PATCH",
            API_ROOT + "companies/" + companyId + "/telegramBotPermissions",
            null,
            (updatedCompany) => {
                setDataMethod((prev) => ({
                    ...prev,
                    telegramBotPermissions: updatedCompany.telegramBotPermissions,
                }));
                setSavingKey(null);
            },
            () => {
                addNotification({ message: "Failed to update bot permissions", type: "alert", duration: "1000" });
                setSavingKey(null);
            },
            next
        );
    };

    return (
        <div className={styles.telegramBotSettings}>
            <div className={styles.telegramBotApiKeyRow}>
                <span className={styles.telegramBotApiKeyValue}>
                    {apiKey || "No API key generated yet"}
                </span>
                {apiKey ? (
                    <button
                        type="button"
                        className={styles.telegramBotCopyBtn}
                        onClick={copyKey}
                        title="Copy API key"
                        aria-label="Copy API key"
                    >
                        <RiFileCopyLine />
                    </button>
                ) : null}
            </div>
            <div className={styles.telegramBotPermissionsLabel}>Allowed Actions</div>
            <div className={styles.telegramBotPermissions}>
                {TELEGRAM_BOT_ACTIONS.map(({ key, label }) => {
                    const allowed = !!perms[key];
                    return (
                        <button
                            key={key}
                            type="button"
                            className={`${styles.telegramBotPermissionChip} ${allowed ? styles.allowed : styles.denied}`}
                            onClick={() => togglePermission(key)}
                            disabled={!!savingKey}
                            aria-pressed={allowed}
                        >
                            <span className={styles.telegramBotPermissionLabel}>{label}</span>
                            <span className={styles.telegramBotPermissionIcon} aria-hidden="true">
                                {allowed ? <FiPlus strokeWidth={3} /> : <FiMinus strokeWidth={3} />}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
